import { useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { useLocale } from '../../i18n/context'
import type { TextFlowSaveResult } from '../../types'
import { useRefreshWhen } from './useRefreshWhen'

/**
 * The toast a save from the creation flow ends with (handoff §16.2): what was made,
 * what waits and until when, or that the budget kept some words out. Every number
 * and time is the server's response. Shown by the screen the save lands on.
 */
export function useSaveToast(): (result: TextFlowSaveResult) => void {
  const { tr } = useLocale()
  const s = tr.textFlow.saved
  const when = useRefreshWhen()
  return useCallback(
    (result) => {
      const waiting = result.waiting.length
      const refresh = when(result.budget.refresh_at)
      if (result.budget_hit) toast(s.toastBudgetHit)
      else if (result.text && result.cards_accepted > 0) {
        toast.success(s.toastSaved(result.cards_accepted, result.text.gaps.length, waiting, refresh))
      } else if (result.text && waiting > 0) {
        toast.success(s.toastSaved(0, result.text.gaps.length, waiting, refresh))
      } else if (result.text) toast.success(s.toastTextOnly(result.text.gaps.length))
      else toast.success(s.toastCards(result.cards_accepted))
      // A cards-only save over the budget has no text to keep the words waiting:
      // they are named, so nothing is lost silently (ADR-115 Decision 8).
      if (!result.text && result.not_created.length > 0) {
        toast(s.notCreated(result.not_created.map((w) => w.word).join(', ')), { duration: 8000 })
      }
    },
    [s, when],
  )
}
